import Dexie, { Table } from "dexie"
import { ImageInfoRecord, PhotoRecord, ProjectInfoRecord } from "@/lib/catalog"
import { debounce } from "@/utils/debounce"
import { uuid } from "@/utils/uuid"
import { Adjustments, LookTable } from "@/core/types"
import { Sentry } from "@/telemetry/sentry"
import { isBrowserStorageQuotaError, isStorageQuotaError } from "@/utils/error"
import {
	PreviewId,
	PreviewSize,
	PreviewTag,
	getPreviewId,
	PreviewInfo,
} from "@/store/previews/previews-types"
import equal from "fast-deep-equal"
import { hasReferenceWebpData256x256Source } from "@/lib/style/reference-webp-data"
import type {
	AIMaskArtifactBundleRecord,
	AIMaskArtifactChunkRecord,
} from "./ai-mask-artifact-types"

export interface KeyValueRecord {
	key: string
	value: any
	updatedAt: Date
}

export interface TimedRecord {
	createdAt: Date
	updatedAt: Date
}

export interface ProjectRecord extends TimedRecord {
	id: string // UUID
	name: string // User defined name
	photoCount?: number
	styleId?: string | null
	sortOption?: string | null
	needsSetup?: boolean | null
	isManual?: boolean | null
	isCulling?: boolean | null
	handle: FileSystemDirectoryHandle
}

export interface ProjectFileHandleRecord {
	id: string
	handle: FileSystemDirectoryHandle
}

export interface PreviewRecord extends TimedRecord {
	id: PreviewId
	projectId: string
	photoId: string
	digest: string
	size: number
	fileSize: number
	data: Blob
	cloudSyncStatus?: string
	cloudUrl?: string
	tags: PreviewTag[]
}

export interface PresetRecord extends TimedRecord {
	id: string
	data?: File | string
	name: string
	folder: string
	adjustments?: Partial<Adjustments>
	readOnly?: boolean
}

export interface ColorMatchImageRecord extends TimedRecord {
	id: string
	data: Blob
	name: string
	contentDigest?: string
	userId?: string
}

export interface MaskGroupThumbnailRecord {
	id: string
	photoKey: string
	projectId: string
	photoId: string
	groupId: string
	transformKey: string
	maskGroupDigest: string
	data: Blob
	byteSize: number
	updatedAt: number
}

export interface DisplayStyleRecord extends TimedRecord {
	id: string
	projectId: string | null
	name: string
	originalId: string
	originalCreatedAt: Date
	referenceCount: number
}

export interface StyleRecord extends DisplayStyleRecord {
	schemaVersion: number
	version: number
	presets: Record<string, Partial<Adjustments>>
	lutUrls?: Record<string, string>
	referenceImages: Record<string, ImageInfoRecord>
	prioritizeImageIds?: Array<string> // Process these photos first when applying style, local styles only
}

export function isFullStyle(style: DisplayStyleRecord): style is StyleRecord {
	return "referenceImages" in style
}

export function isDateEqual(a: Date | string, b: Date | string) {
	const aDate = a instanceof Date ? a : new Date(a)
	const bDate = b instanceof Date ? b : new Date(b)
	return aDate.getTime() === bDate.getTime()
}

export function isStyleEqual(a: StyleRecord, b: StyleRecord) {
	// Removed large data in the style record for fast comparison
	const simplifiedA = getSimplifiedStyle(a)
	const simplifiedB = getSimplifiedStyle(b)

	return equal(simplifiedA, simplifiedB)
}

export function isImageInfoEqual(a: ImageInfoRecord, b: ImageInfoRecord) {
	// Removed large data in the image info record for fast comparison
	const simplifiedA = simplifyImageInfo(a)
	const simplifiedB = simplifyImageInfo(b)

	return equal(simplifiedA, simplifiedB)
}

function simplifyImageInfo(imageInfo: ImageInfoRecord) {
	const simplifiedImageInfo = {
		imageId: imageInfo.imageId,
		presetId: imageInfo.presetId,
		biases: imageInfo.biases,
		features: {
			wb: imageInfo.features?.wb,
			light: imageInfo.features?.light,
			scene: imageInfo.features?.scene?.sceneFeature,
			colorMatch: imageInfo.features?.colorMatch?.avgFaceHsvl,
		},
		colorMatchReference: {
			avgFaceHsvl: imageInfo.colorMatchReference?.avgFaceHsvl,
			imageId: imageInfo.colorMatchReference?.imageId,
			adjustmentsDigest: imageInfo.colorMatchReference?.adjustmentsDigest,
			hasWebp: hasReferenceWebpData256x256Source(imageInfo),
		},
	}
	return simplifiedImageInfo
}

function getSimplifiedStyle(style: StyleRecord) {
	const referenceImages = Object.entries(style.referenceImages).map(([key, imageInfo]) => {
		return { [key]: simplifyImageInfo(imageInfo) }
	})
	return { ...style, referenceImages }
}

export interface GrayImageData {
	data: Uint8Array
	width: number
	height: number
}

export interface FlowFeatureRecord extends TimedRecord {
	id: string
	feature: Float32Array // 1944x128 floats (= 432x288x2 floats)
	grayImage: GrayImageData // 432x288x1 gray image
}

export class Database extends Dexie {
	keyval!: Table<KeyValueRecord, string>
	projects!: Table<ProjectRecord, string>
	projectFileHandles!: Table<ProjectFileHandleRecord, string>
	presets!: Table<PresetRecord, string>
	previews!: Table<PreviewRecord, string>
	styles!: Table<StyleRecord, string>
	flowFeatures!: Table<FlowFeatureRecord, string>
	colorMatchImages!: Table<ColorMatchImageRecord, string>
	aiMaskArtifactBundles!: Table<AIMaskArtifactBundleRecord, string>
	aiMaskArtifactChunks!: Table<AIMaskArtifactChunkRecord, string>
	maskGroupThumbnails!: Table<MaskGroupThumbnailRecord, string>

	constructor() {
		super("polarr-next")

		this.version(2).stores({
			keyval: "&key, updatedAt",
			projects: "&id, directoryName",
			projectFileHandles: "&id",
			previews: "&id, projectId, createdAt, updatedAt, [projectId+cloudSyncStatus]",
			presets: "id, folder, name",
			styles: "id, projectId, updatedAt",
		})

		this.version(3).stores({
			flowFeatures: "&id, updatedAt",
		})

		// version 4 and 5 were used to migrating previous previews, we decided to skip those and wipe all previous previews so we are onto version 6
		this.version(6)
			.stores({
				previews:
					"&id, projectId, createdAt, updatedAt, [projectId+cloudSyncStatus], tags, size",
			})
			.upgrade(async (tx) => {
				try {
					await tx.table("previews").clear()
				} catch (error) {
					console.error("Failed to clear previews table for migration", error)
					Sentry.captureException(
						new Error("Failed to clear previews table for migration", { cause: error }),
						{ level: "warning" },
					)
				}
			})

		this.version(7).stores({
			colorMatchImages: "&id, createdAt, updatedAt",
		})

		this.version(8).upgrade(async (tx) => {
			return tx
				.table("styles")
				.toCollection()
				.modify((style: StyleRecord) => {
					style.referenceCount = Object.keys(style.referenceImages).length
				})
		})

		this.version(9).stores({
			// Disposable AI inference cache. Bundles are manifests; chunks hold selectively readable pixels.
			aiMaskArtifactBundles:
				"&id, photoKey, projectId, photoId, kind, lastAccessedAt, pipelineSignature",
			aiMaskArtifactChunks: "&id, bundleId",
		})

		this.version(10).stores({
			maskGroupThumbnails: "&id, photoKey, projectId, photoId, updatedAt",
		})
	}
}

const maxKeyValueCacheSize = 1000 // Maximum cache entries
const maxPreviewCacheSize = 2048 * 1024 * 1024 // Maximum cache size in bytes
const maxFlowFeatureCacheSize = 384 * 1024 * 1024 // Maximum flow feature cache size in bytes; each entry is about 1MB

export const db = new Database()

let quotaRecoveryPromise: Promise<void> | null = null

async function performIndexedDBQuotaRecovery(error: unknown) {
	let estimate: StorageEstimate = {}
	if (typeof navigator !== "undefined" && navigator.storage?.estimate) {
		try {
			estimate = (await navigator.storage.estimate()) ?? {}
		} catch {
			// Storage estimates are diagnostic only and must not prevent quota recovery.
		}
	}
	const [bundles, artifactChunkCount, thumbnails] = await Promise.all([
		db.aiMaskArtifactBundles.toArray(),
		db.aiMaskArtifactChunks.count(),
		db.maskGroupThumbnails.toArray(),
	])
	const artifactBytes = bundles.reduce((total, bundle) => total + bundle.byteSize, 0)
	const thumbnailBytes = thumbnails.reduce((total, thumbnail) => total + thumbnail.byteSize, 0)

	Sentry.captureException(error, {
		tags: {
			quota: estimate.quota,
			usage: estimate.usage,
			remaining:
				estimate.quota && estimate.usage ? estimate.quota - estimate.usage : undefined,
			keyvals: await db.keyval.count(),
			previews: await db.previews.count(),
			presets: await db.presets.count(),
			styles: await db.styles.count(),
			flowFeatures: await db.flowFeatures.count(),
			projects: await db.projects.count(),
			projectHandles: await db.projectFileHandles.count(),
			aiMaskArtifactBundles: bundles.length,
			aiMaskArtifactChunks: artifactChunkCount,
			aiMaskArtifactBytes: artifactBytes,
			maskGroupThumbnails: thumbnails.length,
			maskGroupThumbnailBytes: thumbnailBytes,
		},
	})

	await db.transaction(
		"rw",
		db.aiMaskArtifactBundles,
		db.aiMaskArtifactChunks,
		db.maskGroupThumbnails,
		async () => {
			await db.aiMaskArtifactChunks.clear()
			await db.aiMaskArtifactBundles.clear()
			await db.maskGroupThumbnails.clear()
		},
	)

	void evictOldKeyValues()
	void evictOldPreviews()
	void evictOldFlowFeatures()
}

export function recoverFromIndexedDBQuota(error: unknown) {
	if (quotaRecoveryPromise) {
		return quotaRecoveryPromise
	}

	quotaRecoveryPromise = performIndexedDBQuotaRecovery(error).finally(() => {
		quotaRecoveryPromise = null
	})
	return quotaRecoveryPromise
}

async function withErrorHandling<T>(operation: Promise<T>): Promise<T> {
	try {
		return await operation
	} catch (error) {
		await handleDBError(error)
		throw error
	}
}

export async function storeKeyValue(key: string, value: any) {
	evictOldKeyValues()

	return withErrorHandling(
		db.keyval.put({
			key,
			value,
			updatedAt: new Date(),
		}),
	)
}

export async function fetchKeyValue(key: string) {
	const record = await withErrorHandling(db.keyval.get({ key }))
	if (record) {
		await storeKeyValue(record.key, record.value)
	}
	return record
}

export async function deleteKeyValue(key: string) {
	return withErrorHandling(db.keyval.delete(key))
}

export const handleDBError = async (error) => {
	if (isBrowserStorageQuotaError(error) || isStorageQuotaError(error) === true) {
		await recoverFromIndexedDBQuota(error).catch((recoveryError) => {
			Sentry.captureException(recoveryError)
		})
	} else {
		Sentry.captureException(error)
	}
	console.error(error)
	throw error
}

export const evictOldKeyValues = debounce(async () => {
	/*
	 Evict items with smaller updatedAt field when item count in
	 collection is bigger than maxKeyValueCacheSize value defined
	 in this file. This follows the cache principle of Least
	 Recently Used (LRU)
	 */

	const itemCount = await db.keyval.count()

	if (itemCount <= maxKeyValueCacheSize) {
		// No evicting happens if we're within allowed limits
		return
	}

	const keyValues = await db.keyval.orderBy("updatedAt").toArray()

	// Otherwise, evict the files created much earlier.

	// Evict 10% of the cache
	const targetEvictionCount = Math.ceil(maxKeyValueCacheSize / 10)
	const evictedItems: string[] = []
	let evictedItemCount = 0

	while (evictedItemCount < targetEvictionCount && keyValues.length > 0) {
		const item = keyValues.shift()

		if (!item) {
			break
		}

		evictedItems.push(item.key)
		evictedItemCount += 1
	}

	await db.previews.bulkDelete(evictedItems)

	console.log(`[IndexedDB] KeyValue: evicted ${evictedItemCount} old items`)
}, 1000)

export async function storePresets(records: PresetRecord[]) {
	return withErrorHandling(db.presets.bulkPut(records))
}

export async function updatePreset(preset: PresetRecord) {
	return withErrorHandling(db.presets.put(preset))
}

export async function deletePreset_deprecated(preset: Pick<PresetRecord, "id">) {
	return withErrorHandling(db.presets.delete(preset.id))
}

export async function fetchPresets_deprecated() {
	return withErrorHandling(db.presets.toArray())
}

export async function fetchPreset(id: string) {
	return withErrorHandling(db.presets.where("id").equals(id).first())
}

export async function fetchPreviewWithTags(props: {
	projectId: string
	photoId: string
	size: PreviewSize
	tags: PreviewTag[]
}) {
	const previewId = getPreviewId(props)
	return await withErrorHandling(
		db.previews
			.where("id")
			.startsWith(previewId)
			.and(function (preview) {
				return props.tags.every((tag) => preview.tags.includes(tag))
			})
			.first(),
	)
}

export async function fetchCurrentPreview(props: {
	projectId: string
	photo: PhotoRecord
	size: PreviewSize
}) {
	if (props.photo.adjustmentsDigest) {
		return fetchEditedPreview(props)
	} else {
		return fetchOriginalPreview({ ...props, photoId: props.photo.id })
	}
}

export async function fetchOriginalPreview(props: {
	projectId: string
	photoId: string
	size: PreviewSize
}) {
	const previewId = getPreviewId({ ...props, photoId: props.photoId })
	return await withErrorHandling(db.previews.get(previewId))
}

export async function fetchEditedPreview(props: {
	projectId: string
	photo: PhotoRecord
	size: PreviewSize
}) {
	const previewId = getPreviewId({
		...props,
		photoId: props.photo.id,
		edited: !!props.photo.adjustmentsDigest,
	})
	const preview = await withErrorHandling(db.previews.get(previewId))
	if (preview && preview.digest === props.photo.adjustmentsDigest) {
		return preview
	} else {
		return undefined
	}
}

export const createPreviewRecord = (info: PreviewInfo): PreviewRecord => {
	const { projectId, photoId, data, tags, digest, size } = info
	const id = getPreviewId(info)
	return {
		id,
		createdAt: new Date(),
		updatedAt: new Date(),
		projectId: projectId,
		photoId: photoId,
		digest: digest ?? "",
		tags: tags,
		fileSize: data.size,
		data,
		size,
	}
}

export async function storePreviewRecords(records: PreviewRecord[]) {
	void evictOldPreviews()
	return withErrorHandling(db.previews.bulkPut(records))
}

export async function storePreview(info: PreviewInfo) {
	void evictOldPreviews()

	const record = createPreviewRecord(info)

	await withErrorHandling(db.previews.put(record))
	return record
}

export async function storePreviewUrl(previewId: string, cloudUrl: string) {
	return withErrorHandling(
		db.previews.update(previewId, {
			cloudSyncStatus: "synced",
			cloudUrl,
		}),
	)
}

export async function removePreviewUrl(preview: PreviewRecord) {
	delete preview["cloudSyncStatus"]
	delete preview["cloudUrl"]
	return withErrorHandling(db.previews.put(preview))
}

export const evictOldPreviews = debounce(async () => {
	// Maximum cache size is whatever is smaller:
	// a) 50% of space currently available to us via quota
	// b) maxPreviewCacheSize value defined in this file
	const browserStorage = await navigator.storage.estimate()
	const maxSize = Math.min(
		maxPreviewCacheSize,
		browserStorage.quota ? browserStorage.quota * 0.5 : maxPreviewCacheSize,
	)

	const previews = await db.previews.orderBy("updatedAt").toArray()

	let currentSize = previews.reduce((previous, current) => {
		if (!current) {
			//This should not happen, log waring so we can investigate
			Sentry.captureException(new Error("[IndexedDB] Invalid preview record"), {
				level: "warning",
			})
			console.warn("[IndexedDB] Invalid preview record")
		}
		return previous + (current?.fileSize ?? 0)
	}, 0)

	if (currentSize <= maxSize) {
		// No evicting happens if we're within allowed limits
		return
	}

	// Otherwise, evict the files created much earlier.

	const evictedPreviews: string[] = []
	let evictedSize = 0

	while (currentSize >= maxSize && previews.length > 0) {
		const preview = previews.shift()

		if (!preview) {
			break
		}

		evictedPreviews.push(preview.id)
		currentSize -= preview.fileSize
		evictedSize += preview.fileSize
	}

	await db.previews.bulkDelete(evictedPreviews)

	console.log(
		`[IndexedDB] Previews: evicted=${evictedPreviews.length} @ ${
			evictedSize / 1024 / 1024
		}MB, left=${previews.length} @ ${currentSize / 1024 / 1024}MB`,
	)
}, 1000)

export async function deletePreview(id: string) {
	return withErrorHandling(db.previews.delete(id))
}

export async function fetchPreviewRecord(projectId, photoId, size, edited: boolean) {
	const previewId = getPreviewId({ projectId, photoId, size, edited })
	return withErrorHandling(db.previews.get(previewId))
}

export async function fetchPreviewsByPhotoIds(
	projectId: string,
	photoIds: string[],
	size: PreviewSize,
	tags?: PreviewTag[],
) {
	const previews = await withErrorHandling(
		db.previews
			.where("projectId")
			.equals(projectId)
			.and(
				(previewRecord) =>
					photoIds.includes(previewRecord.photoId) &&
					previewRecord.size === size &&
					(!tags || tags.every((tag) => previewRecord.tags.includes(tag))),
			)
			.toArray(),
	)

	const selectedPreviews: Map<string, PreviewRecord> = new Map()
	previews.forEach((preview) => {
		const existingPreview = selectedPreviews.get(preview.photoId)
		if (!existingPreview || preview.createdAt > existingPreview.createdAt) {
			selectedPreviews.set(preview.photoId, preview)
		}
	})

	return selectedPreviews
}

export async function fetchPreviews(projectId: string, limit: number) {
	const previews = await withErrorHandling(
		db.previews
			.where("projectId")
			.equals(projectId)
			.and((preview) => preview.size >= 1200)
			.limit(limit * 10)
			.reverse()
			.sortBy("updatedAt"),
	)

	const uniquePhotoIds = new Map()

	const latestUniqueRecords: PreviewRecord[] = []
	for (const record of previews) {
		if (!uniquePhotoIds.has(record.photoId)) {
			latestUniqueRecords.push(record)
			uniquePhotoIds.set(record.photoId, true)
		}
		if (latestUniqueRecords.length === limit) {
			break
		}
	}

	return latestUniqueRecords
}

export async function migratePreviewProjectId(previewId: string, newProjectId: string) {
	return withErrorHandling(
		db.previews.update(previewId, {
			projectId: newProjectId,
		}),
	)
}

export async function fetchSyncedPreviews(projectId: string) {
	return withErrorHandling(db.previews.where({ projectId, cloudSyncStatus: "synced" }).toArray())
}

export async function clearAllPreviews(projectId: string) {
	return withErrorHandling(
		db.previews
			.where({
				projectId: projectId,
			})
			.delete(),
	)
}

export async function clearEditedPreviews(projectId: string) {
	return withErrorHandling(
		db.previews
			.where({
				projectId: projectId,
			})
			.and((preview) => preview.tags.includes("edited"))
			.delete(),
	)
}
export async function fetchProjects_deprecated() {
	return withErrorHandling(db.projects.reverse().toArray())
}

export async function deleteProject_deprecated(project: Pick<ProjectRecord, "id">) {
	return withErrorHandling(db.projects.delete(project.id))
}

export async function fetchProject(id: string) {
	return withErrorHandling(db.projects.where("id").equals(id).first())
}

export function createProjectRecord(
	folder: FileSystemDirectoryHandle,
	projectInfo: Partial<ProjectInfoRecord> = {},
): ProjectRecord {
	return {
		createdAt: new Date(),
		updatedAt: new Date(),
		name: folder.name,
		handle: folder,
		id: projectInfo.id ?? uuid(),
		...projectInfo,
	}
}

export async function storeProject(project: ProjectRecord) {
	return withErrorHandling(db.projects.put(project))
}

export async function storeProjectFileHandle(projectFileHandle: ProjectFileHandleRecord) {
	return withErrorHandling(db.projectFileHandles.put(projectFileHandle))
}

export async function deleteProjectFileHandle(projectId: string) {
	return withErrorHandling(db.projectFileHandles.delete(projectId))
}

export async function fetchProjectFileHandle(projectId: string) {
	return withErrorHandling(db.projectFileHandles.where("id").equals(projectId).first())
}

export async function fetchProjectFileHandles() {
	const handles = await withErrorHandling(db.projectFileHandles.toArray())
	const handlesMap: Map<string, FileSystemDirectoryHandle> = new Map()
	handles.forEach((item) => {
		handlesMap.set(item.id, item.handle)
	})
	return handlesMap
}

export async function storeFlowFeature(flowFeature: FlowFeatureRecord) {
	evictOldFlowFeatures()

	return withErrorHandling(db.flowFeatures.put(flowFeature))
}

export async function fetchFlowFeature(imageId: string): Promise<FlowFeatureRecord | undefined> {
	return await withErrorHandling(db.flowFeatures.get(imageId))
}

export async function flowFeatureExists(imageId: string) {
	const count = await withErrorHandling(db.flowFeatures.where({ id: imageId }).count())
	return count > 0
}

export const evictOldFlowFeatures = debounce(async () => {
	// Maximum cache size is whatever is smaller:
	// a) 25% of space currently available to us via quota
	// b) maxFlowFeatureCacheSize value defined in this file
	const browserStorage = await navigator.storage.estimate()
	const maxSize = Math.min(
		maxFlowFeatureCacheSize,
		browserStorage.quota ? browserStorage.quota * 0.25 : maxFlowFeatureCacheSize,
	)

	const fileSize = 1119796 // 1944×128×4 + 432×288×1 + 16 + 20 + 16

	let currentSize = (await db.flowFeatures.count()) * fileSize

	if (currentSize <= maxSize) {
		// No evicting happens if we're within allowed limits
		return
	}

	// Otherwise, evict the files created much earlier.
	const items = await db.flowFeatures.orderBy("updatedAt").toArray()

	const evictedFlowFeatures: string[] = []
	let evictedSize = 0

	while (currentSize >= maxSize && items.length > 0) {
		const item = items.shift()

		if (!item) {
			break
		}

		evictedFlowFeatures.push(item.id)
		currentSize -= fileSize
		evictedSize += fileSize
	}

	await db.flowFeatures.bulkDelete(evictedFlowFeatures)

	console.log(
		`[IndexedDB] FlowFeatures: evicted=${evictedFlowFeatures.length} @ ${
			evictedSize / 1024 / 1024
		}MB, left=${items.length} @ ${currentSize / 1024 / 1024}MB`,
	)
}, 1000)

export async function fetchColorMatchImages(): Promise<ColorMatchImageRecord[]> {
	return await withErrorHandling(db.colorMatchImages.toArray())
}

export async function storeColorMatchImages(records: ColorMatchImageRecord[]) {
	return withErrorHandling(db.colorMatchImages.bulkPut(records))
}

export async function storeColorMatchImage(record: ColorMatchImageRecord) {
	return withErrorHandling(db.colorMatchImages.put(record))
}

export async function deleteColorMatchImage(id: string) {
	return withErrorHandling(db.colorMatchImages.delete(id))
}

const STYLE_LUT_KEY_PREFIX = "style-lut|"

/**
 * Store a LookTable in IndexedDB under `style-lut|{lutId}`.
 * Always available regardless of whether a project catalog is open.
 */
export async function storeStyleLut(lookTable: { id: string }) {
	await storeKeyValue(`${STYLE_LUT_KEY_PREFIX}${lookTable.id}`, lookTable)
}

/**
 * Retrieve a LookTable from IndexedDB by lutId.
 * Returns null if not found.
 */
export async function getStyleLut(lutId: string): Promise<LookTable | null> {
	const entry = await fetchKeyValue(`${STYLE_LUT_KEY_PREFIX}${lutId}`)
	return (entry?.value as LookTable) ?? null
}
